import { isEnterKey, Modal, ModalOverlay, NoResults } from "@smartsuite/react-ui/lib";
import { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import cx from "classnames";
import { useKeyboardNavigation } from "@smartsuite/react-ui/lib/hooks/useKeyboardNavigation/useKeyboardNavigation";
import { useEventListener } from "@smartsuite/react-ui/lib/helpers/useEventListener";
import { onKeyDownData } from "@smartsuite/react-ui/lib/hooks/useKeyboardNavigation/types";
import { AppField, ID, SimpleRecord } from "@smartsuite/types";

import { SelectLinkedRecordModalHeader } from "./components/SelectLinkedRecordModalHeader/SelectLinkedRecordModalHeader";
import { SelectLinkedRecordModalFooter } from "./components/SelectLinkedRecordModalFooter/SelectLinkedRecordModalFooter";

import { useFormContext } from "../../../../../../hooks/useFormContext";
import { UseFormFieldResult } from "../../../../../../hooks/useFormField";
import { $t } from "../../../../../../utils/intl";
import { SelectLinkedRecordModalText } from "./SelectLinkedRecordModal.text";
import { SelectRecordPreloader } from "./components/SelectRecordPreloader/SelectRecordPreloader";
import { RecordList } from "./components/RecordList/RecordList";
import { isMacOs } from "../../../../../../utils/devices";
import { useLinkedRecords } from "../../hooks/useLinkedRecords";
import { useLinkedRecordRelatedRecords } from "../../hooks/useLinkedRecordRelatedRecords";
import {
  buildRecordSearchText,
  getSortedRecords,
  getVisibleFields,
} from "../../linked-records.helpers";
import {
  FormConfigApplicationStructureItem,
  FormConfigItem,
  LinkedRecordObject,
} from "../../../../../../types/form";

import "./SelectLinkedRecordModal.sass";
import { useLinkedRecordCoverImageField } from "../../hooks/useLinkedRecordCoverImageField";

interface SelectLinkedRecordModalProps {
  onSave: (selectedRecords: LinkedRecordObject[]) => void;
  field: FormConfigApplicationStructureItem;
  formField: UseFormFieldResult<LinkedRecordObject[]>;
  formItem: FormConfigItem;
  onClose: () => void;
  showFooter?: boolean;
  alreadySelectedRecords?: LinkedRecordObject[];
}

export const SelectLinkedRecordModal: FC<SelectLinkedRecordModalProps> = ({
  field,
  formItem,
  formField,
  onClose,
  onSave,
  showFooter,
  alreadySelectedRecords,
}) => {
  const recordListRef = useRef<HTMLDivElement>(null);

  const formContext = useFormContext();
  const { values } = useFormikContext();

  const [selectedRecords, setSelectedRecords] = useState<ID[]>([]);
  const [options, setOptions] = useState<SimpleRecord[]>([]);

  const classes = cx("select-linked-record-modal", {
    "no-footer": !showFooter,
  });

  const {
    isMultiple,
    linkedApplicationFields,
    records,
    selectableRecords,
    isRecordsLoading,
    linkedAppStructureLoading,
    searchValue,
    setSearchValue,
  } = useLinkedRecords({
    field,
    formItem,
    formField,
  });

  const linkedRecordCoverImageField = useLinkedRecordCoverImageField({
    params: formItem.params.cover_image_field !== undefined ? formItem.params : field.params,
    linkedApplicationFields,
  });

  const visibleFields = getVisibleFields(formItem, field);

  const { data: relatedRecords, isLoading: relatedRecordsLoading } = useLinkedRecordRelatedRecords({
    field,
    visibleFields,
  });

  const fieldsToShow = visibleFields.reduce(
    (acc: { [key: string]: AppField }, fieldSlug: string) => {
      const field = linkedApplicationFields?.[fieldSlug];

      if (field?.slug === "title") return acc;

      if (field) {
        acc[fieldSlug] = field;
      }

      if (fieldSlug.includes(".")) {
        // Nested field
        const mainFieldSlug = fieldSlug.split(".")[0];
        const mainField = linkedApplicationFields?.[mainFieldSlug];

        if (mainField) {
          acc[fieldSlug] = mainField;
        }
      }

      return acc;
    },
    {}
  );

  const tags = formContext.config.tags;

  const searchIndex = useMemo(
    () =>
      new Map(
        (selectableRecords ?? []).map((record) => [
          record.id,
          buildRecordSearchText(record, fieldsToShow, tags, relatedRecords),
        ])
      ),
    [selectableRecords, Object.keys(fieldsToShow).join(","), relatedRecords, tags]
  );

  useEffect(() => {
    const unselectedRecords =
      selectableRecords?.filter(
        (record) =>
          !alreadySelectedRecords?.find((selectedRecord) => selectedRecord.id === record.id)
      ) ?? [];

    const lowerSearch = searchValue.toLowerCase();

    const filteredRecords = unselectedRecords
      .sort((lr1, lr2) => lr1.title.toLowerCase().localeCompare(lr2.title.toLowerCase()))
      .filter((option) => {
        if (!searchValue) {
          return true;
        }

        return searchIndex.get(option.id)?.includes(lowerSearch) ?? false;
      });

    const sortedRecords = getSortedRecords({
      records: filteredRecords,
      formItem,
      field,
      linkedApplicationFields,
      preferences: formContext.preferences,
    });

    setOptions(sortedRecords);
  }, [
    selectableRecords,
    field,
    formItem,
    formField.value,
    formContext.config.application.structure,
    formContext.config.form_state.items,
    isMultiple,
    values,
    searchValue,
    alreadySelectedRecords,
    linkedApplicationFields,
    searchIndex,
  ]);

  const handleSave = useCallback(
    (selectedRecords: ID[]): void => {
      const selectedRecordsData = selectedRecords.reduce((acc: LinkedRecordObject[], record) => {
        const recordData: SimpleRecord | undefined = records?.find((r) => r.id === record);

        if (recordData) {
          return [
            ...acc,
            {
              id: recordData.id.toString(),
              title: recordData.title,
            },
          ];
        }

        return acc;
      }, []);

      onSave(
        isMultiple
          ? [...(alreadySelectedRecords ?? []), ...selectedRecordsData]
          : selectedRecordsData
      );

      onClose();
    },
    [alreadySelectedRecords, isMultiple, onClose, onSave, records]
  );

  const handleRecordClick = useCallback(
    (recordId: ID): void => {
      const newSelectedRecords = selectedRecords.includes(recordId)
        ? selectedRecords.filter((id) => id !== recordId)
        : [...selectedRecords, recordId];

      setSelectedRecords(newSelectedRecords);

      if (!isMultiple) {
        handleSave(newSelectedRecords);
      }
    },
    [handleSave, isMultiple, selectedRecords]
  );

  const loading = isRecordsLoading || linkedAppStructureLoading || relatedRecordsLoading;

  const handleSaveKeyPress = useCallback(
    (event: Event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (isMacOs() && keyboardEvent.metaKey) {
        if (keyboardEvent.key === "s" || keyboardEvent.code === "KeyS") {
          event.preventDefault();
          event.stopPropagation();
          handleSave(selectedRecords);
          return;
        }
      }
      if (keyboardEvent.ctrlKey && (keyboardEvent.key === "s" || keyboardEvent.code === "KeyS")) {
        event.preventDefault();
        event.stopPropagation();
        handleSave(selectedRecords);
      }
    },
    [handleSave, selectedRecords]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent, data: onKeyDownData<SimpleRecord>) => {
      if (isEnterKey(event) && data?.item) {
        handleRecordClick(data.item.id);
      }
    },
    [handleRecordClick]
  );

  const { cursor, focusedItem } = useKeyboardNavigation<SimpleRecord>({
    ref: recordListRef as MutableRefObject<HTMLDivElement | undefined>,
    items: options,
    enableTabNavigation: true,
    onKeyDown: handleKeyDown,
  });

  useEventListener(document.body, "keydown", handleSaveKeyPress, !!selectedRecords?.length);

  return (
    <ModalOverlay active backdrop onClose={onClose}>
      <Modal
        ariaLabel="Select linked records modal"
        className={classes}
        withoutHat
        size="L"
        ref={recordListRef}
        hatHeight={0}
        header={
          <SelectLinkedRecordModalHeader
            searchValue={searchValue}
            onChangeSearchValue={setSearchValue}
            onClose={onClose}
          />
        }
        footer={
          <SelectLinkedRecordModalFooter
            selectedRecordsLength={selectedRecords.length}
            onClose={onClose}
            onSave={() => handleSave(selectedRecords)}
          />
        }
      >
        {!loading && !options?.length && (
          <NoResults
            className="select-linked-record-modal__no-found"
            img="/form/images/no-search-results.svg"
            imgGutter={false}
            title={$t(SelectLinkedRecordModalText.searchPlaceholder)}
          />
        )}
        {loading && <SelectRecordPreloader />}

        {!loading && options?.length > 0 && (
          <RecordList
            coverImageField={linkedRecordCoverImageField}
            fieldsToShow={fieldsToShow}
            linkedAppFields={linkedApplicationFields}
            records={options}
            selectedRecords={selectedRecords}
            onRecordClick={handleRecordClick}
            relatedRecords={relatedRecords}
            focusedItem={focusedItem}
            cursor={cursor}
          />
        )}
      </Modal>
    </ModalOverlay>
  );
};
